import type {HTMLChakraProps} from '@chakra-ui/react';
import {Skeleton, SkeletonText} from '@chakra-ui/react';
import {ChakraDiv, ChakraLabel} from '@linx-security/ui/utils/chakra-elements';
import type {ReactNode} from 'react';

import {parts} from './theme/anatomy';
import {useStyles} from './theme/context';

export const NumbersContainer = ({
  label,
  left,
  right,
  isLoaded,
  ...props
}: Omit<HTMLChakraProps<`div`>, 'children' | 'right' | 'left'> & {
  label: string;
  right?: string | number | ReactNode;
  left?: string | number | ReactNode;
  isLoaded?: boolean;
}) => {
  const {container: __css, numbersContainer} = useStyles();
  return (
    <ChakraDiv __css={__css} {...props} {...parts.container.attrs}>
      {label && <NumbersLabel isLoaded={isLoaded}>{label}</NumbersLabel>}
      <Skeleton isLoaded={isLoaded} minH={`2rem`} minW={`5rem`}>
        <ChakraDiv __css={numbersContainer} {...parts.numbersContainer.attrs}>
          {left && <NumbersLeft>{left}</NumbersLeft>}
          {right && <NumbersRight>{right}</NumbersRight>}
        </ChakraDiv>
      </Skeleton>
    </ChakraDiv>
  );
};

export const NumbersLabel = ({children, isLoaded, ...props}: HTMLChakraProps<`div`> & {isLoaded?: boolean}) => {
  const {label: __css} = useStyles();
  return (
    <SkeletonText isLoaded={isLoaded} noOfLines={1} mb={isLoaded ? `` : `space-100`}>
      <ChakraLabel __css={__css} {...props} {...parts.label.attrs}>
        {children}
      </ChakraLabel>
    </SkeletonText>
  );
};

export const NumbersLeft = ({children, ...props}: HTMLChakraProps<`div`>) => {
  const {left: __css} = useStyles();
  return (
    <ChakraDiv __css={__css} {...props} {...parts.left.attrs}>
      {(typeof children === `string` || typeof children === `number`
        ? Intl.NumberFormat().format(+children)
        : children) ?? `N/A`}
    </ChakraDiv>
  );
};

export const NumbersRight = ({children, ...props}: HTMLChakraProps<`div`>) => {
  const {right: __css} = useStyles();
  return (
    <ChakraDiv __css={__css} {...props} {...parts.right.attrs}>
      {` `}/{` `}
      {(typeof children === `string` || typeof children === `number`
        ? Intl.NumberFormat().format(+children)
        : children) ?? `N/A`}
    </ChakraDiv>
  );
};
